Object.defineProperty(exports, "__esModule", {
    value: true
});
Object.defineProperty(exports, "Script", {
    enumerable: true,
    get: function() {
        return Script;
    }
});
var _class_call_check = require("@swc/helpers/_/_class_call_check");
var _create_class = require("@swc/helpers/_/_create_class");
var _interop_require_default = require("@swc/helpers/_/_interop_require_default");
var _shallowequal = /*#__PURE__*/ _interop_require_default._(require("shallowequal"));
var _NativeScriptManager = require("./NativeScriptManager.js");
var Script = /*#__PURE__*/ function() {
    "use strict";
    function Script(scriptId, caller, locator) {
        var cache = arguments.length > 3 && arguments[3] !== void 0 ? arguments[3] : true;
        _class_call_check._(this, Script);
        this.scriptId = scriptId;
        this.caller = caller;
        this.locator = locator;
        this.cache = cache;
    }
    _create_class._(Script, [
        {
            /**
   * Check if the script was already cached and cache should be updated with new data.
   *
   * @param cachedData Cached data for the same script.
   *
   * @internal
   */ key: "shouldUpdateCache",
            value: function shouldUpdateCache(cachedData) {
                if (!this.cache || !cachedData) {
                    return false;
                }
                return this.checkIfCacheDataOutdated(cachedData);
            }
        },
        {
            /**
   * Check if the script should be fetched again or reused,
   * based on previous cached data.
   *
   * @param cachedData Cached data for the same script.
   *
   * @internal
   */ key: "shouldRefetch",
            value: function shouldRefetch(cachedData) {
                if (!this.cache) {
                    return true;
                }
                return this.checkIfCacheDataOutdated(cachedData);
            }
        },
        {
            /**
   * Check if previous cached data is the same as the new one.
   *
   * @param cachedData Cached data for the same script.
   *
   * @internal
   */ key: "checkIfCacheDataOutdated",
            value: function checkIfCacheDataOutdated(cachedData) {
                return cachedData.method !== this.locator.method || cachedData.url !== this.locator.url || cachedData.query !== this.locator.query || !(0, _shallowequal.default)(cachedData.headers, this.locator.headers) || cachedData.body !== this.locator.body;
            }
        },
        {
            /**
   * Get object to store in cache.
   *
   * @internal
   */ key: "getCacheData",
            value: function getCacheData() {
                return {
                    method: this.locator.method,
                    url: this.locator.url,
                    query: this.locator.query,
                    headers: this.locator.headers,
                    body: this.locator.body
                };
            }
        },
        {
            key: "toObject",
            value: function toObject() {
                return {
                    scriptId: this.scriptId,
                    caller: this.caller,
                    locator: this.locator,
                    cache: this.cache
                };
            }
        }
    ], [
        {
            key: "getDevServerURL",
            value: /**
   * Get URL of a script hosted on development server.
   *
   * @param scriptId Id of the script.
   */ function getDevServerURL(scriptId) {
                return (webpackContext)=>`${webpackContext.p}${webpackContext.u(scriptId)}`;
            }
        },
        {
            key: "getFileSystemURL",
            value: /**
   * Get URL of a script stored on filesystem on the target mobile device.
   *
   * @param scriptId Id of the script.
   */ function getFileSystemURL(scriptId) {
                return (webpackContext)=>webpackContext.u(`file:///${scriptId}`);
            }
        },
        {
            key: "getRemoteURL",
            value: /**
   * Get URL of a script hosted on a remote server.
   *
   * By default `.chunk.bundle` extension will be added to the URL.
   * If your script has different extension, you should pass `{ excludeExtension: true }` as 2nd argument.
   *
   * @param url A URL to remote location where the script is stored.
   * @param options Additional options.
   */ function getRemoteURL(url) {
                var options = arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : {};
                if (options.excludeExtension) {
                    return url;
                }
                return (webpackContext)=>webpackContext.u(url);
            }
        },
        {
            key: "getScriptUniqueId",
            value: /**
   * Get unique identifier for the script.
   *
   * Used to create unique identifier for the script, which serves as its key in the cache.
   *
   * @param scriptId Id of the script.
   * @param caller Optional caller name to prefix the script id.
   */ function getScriptUniqueId(scriptId, caller) {
                var prefix = caller ? caller + '_' : '';
                return prefix + scriptId;
            }
        },
        {
            key: "from",
            value: /**
   * Create new instance of `Script` from non-normalized script locator data.
   *
   * @param locator Non-normalized locator data.
   * @param fetch Initial flag for whether script should be fetched or not.
   *
   * @internal
   */ function from(key, locator, fetch) {
                var headers = {};
                new Headers(locator.headers).forEach((value, key)=>{
                    headers[key.toLowerCase()] = value;
                });
                var uniqueId = Script.getScriptUniqueId(key.scriptId, key.caller);
                var body;
                if (locator.body instanceof FormData) {
                    var bodyObject = {};
                    locator.body.forEach((value, key)=>{
                        if (typeof value === 'string') {
                            bodyObject[key] = value;
                        } else {
                            console.warn('Script does not support File as FormData key in body');
                        }
                    });
                    body = JSON.stringify(bodyObject);
                } else if (locator.body instanceof URLSearchParams) {
                    var bodyObject1 = {};
                    locator.body.forEach((value, key)=>{
                        bodyObject1[key] = value;
                    });
                    body = JSON.stringify(bodyObject1);
                } else {
                    body = locator.body ?? undefined;
                }
                if (typeof locator.url === 'function') {
                    throw new Error('Property url as a function is not support');
                }
                return new Script(key.scriptId, key.caller, {
                    uniqueId,
                    method: locator.method ?? _NativeScriptManager.NormalizedScriptLocatorHTTPMethod.GET,
                    url: locator.url,
                    absolute: locator.absolute ?? false,
                    timeout: locator.timeout ?? Script.DEFAULT_TIMEOUT,
                    retry: locator.retry,
                    retryDelay: locator.retryDelay,
                    query: new URLSearchParams(locator.query).toString() || undefined,
                    body,
                    headers: Object.keys(headers).length ? headers : undefined,
                    fetch: locator.cache === false ? true : fetch,
                    verifyScriptSignature: locator.verifyScriptSignature ?? _NativeScriptManager.NormalizedScriptLocatorSignatureVerificationMode.OFF
                }, locator.cache);
            }
        }
    ]);
    return Script;
}();
Script.DEFAULT_TIMEOUT = 30000; // 30s
